import { Link } from "react-router-dom";
import type { ComponentType, SVGProps } from "react";
import {
  TimelineIcon,
  MailIcon,
  SparkleIcon,
  BoltIcon,
  BagIcon,
  UsersIcon,
  BookIcon,
  PortalIcon,
  ArrowRightIcon,
  ChatIcon,
  PhoneIcon,
  ShieldIcon,
  CheckIcon,
  LayersIcon,
  GaugeIcon,
  CodeIcon,
  LockIcon,
  RouteIcon,
  TagIcon,
  GlobeIcon,
  ChartIcon,
  StopwatchIcon,
  RefreshIcon,
  InboxIcon,
  BuildingIcon,
  PlugIcon,
} from "../components/Icons";
import { GridPattern } from "../components/Patterns";
import { SITE } from "../config";
import { usePageMeta } from "../seo";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

type Group = {
  id: string;
  heading: string;
  Icon: IconType;
  subheading: string;
  items: { title: string; body: string; Icon: IconType }[];
};

const groups: Group[] = [
  {
    id: "channels",
    heading: "Email, WhatsApp and phone",
    Icon: InboxIcon,
    subheading:
      "Three channels today, each turning conversations into tickets on the same timeline.",
    items: [
      {
        Icon: RefreshIcon,
        title: "Forward your support address",
        body:
          "Forward support@, sales@ or billing@ to ZopDesk. Each address becomes a queue you can filter and write rules for.",
      },
      {
        Icon: MailIcon,
        title: "Or connect a mailbox",
        body:
          "Sign in with Gmail or Microsoft 365, or use any IMAP mailbox with an app password. ZopDesk checks it every 30 seconds.",
      },
      {
        Icon: ShieldIcon,
        title: "Replies from your own address",
        body:
          "Replies go out through your own mailbox or SMTP server, from the address the customer wrote to, threaded on the original email.",
      },
      {
        Icon: ChatIcon,
        title: "WhatsApp",
        body:
          "Connect a WhatsApp Business number through Meta's Cloud API. Messages, photos, documents and voice notes become tickets; agents reply within WhatsApp's 24-hour window.",
      },
      {
        Icon: PhoneIcon,
        title: "Phone via Acefone",
        body:
          "Incoming and missed calls become tickets. Agents press Call on a ticket to ring the customer, and each call's duration and recording appear on the timeline.",
      },
    ],
  },
  {
    id: "ticketing",
    heading: "Ticketing",
    Icon: TimelineIcon,
    subheading:
      "One timeline per ticket, and the everyday tools to work through a queue together.",
    items: [
      {
        Icon: TimelineIcon,
        title: "One timeline",
        body:
          "Customer messages, replies, internal notes, calls, status changes and assignments, in order, on one ticket.",
      },
      {
        Icon: TagIcon,
        title: "Status, priority, tags, assignee",
        body:
          "Open, pending, on hold, solved and closed; four priorities; tags; one assignee. A customer reply reopens a solved ticket.",
      },
      {
        Icon: LayersIcon,
        title: "Attachments and internal notes",
        body:
          "Files on inbound email and WhatsApp are kept on the ticket, and agents can attach files up to 10 MB to replies and notes.",
      },
      {
        Icon: UsersIcon,
        title: "See who else is on a ticket",
        body:
          "When a teammate is viewing or replying to the same ticket, you see it, so two people don't answer the same customer.",
      },
      {
        Icon: CodeIcon,
        title: "Custom fields",
        body:
          "Define your own ticket and customer fields (text, choice, number, yes/no, date) and filter by them.",
      },
      {
        Icon: BuildingIcon,
        title: "Companies",
        body:
          "Group the people from one business into a company by email domain, and see everyone's tickets together.",
      },
    ],
  },
  {
    id: "automation",
    heading: "Saved replies, rules and SLAs",
    Icon: BoltIcon,
    subheading:
      "The repetitive parts of support, written down once.",
    items: [
      {
        Icon: BookIcon,
        title: "Saved replies with variables",
        body:
          "{{customer.first_name}}, {{agent.name}}, {{ticket.subject}}, {{org.name}} and more fill themselves in when an agent inserts a saved reply.",
      },
      {
        Icon: RouteIcon,
        title: "Rules",
        body:
          "On a new ticket or a customer reply, match on channel, queue, sender, subject or message text. Then assign, round-robin, pick the least busy agent, tag, set priority, or close quietly.",
      },
      {
        Icon: RefreshIcon,
        title: "Auto-close",
        body:
          "Close tickets that have been pending for a set number of days, with an optional message to the customer.",
      },
      {
        Icon: StopwatchIcon,
        title: "SLA targets",
        body:
          "First-reply and resolution targets per priority, counted in your business hours and time zone. Tickets show when they're due; there are no breach alerts yet.",
      },
      {
        Icon: CheckIcon,
        title: "Satisfaction survey",
        body:
          "When a ticket is solved, the customer gets a one-tap good/bad survey, but only if an agent replied. Leave out queues where surveys don't make sense.",
      },
      {
        Icon: ChartIcon,
        title: "Reports",
        body:
          "Tickets created and solved, median first-reply and resolution times, SLA met or missed and survey results, by day, channel, queue and agent.",
      },
    ],
  },
  {
    id: "ai",
    heading: "AI assist, with your own key",
    Icon: SparkleIcon,
    subheading:
      "Optional. Each org connects its own OpenAI or Anthropic key, and agents stay in charge of every reply.",
    items: [
      {
        Icon: SparkleIcon,
        title: "Draft a reply",
        body:
          "Proposes a reply from the ticket's conversation. The agent edits it in the reply box and sends it; nothing goes out on its own.",
      },
      {
        Icon: BookIcon,
        title: "Summarize a thread",
        body:
          "A short summary of a long conversation, for when a ticket changes hands.",
      },
      {
        Icon: LockIcon,
        title: "Your provider, your key",
        body:
          "Pick OpenAI or Anthropic and a model, and paste your API key. It's stored encrypted, and usage is billed by your provider.",
      },
      {
        Icon: GaugeIcon,
        title: "Usage dashboard",
        body:
          "See how many AI requests and tokens your org has used.",
      },
    ],
  },
  {
    id: "integrations",
    heading: "Customer context and your own systems",
    Icon: BagIcon,
    subheading:
      "What agents need to know about a customer, next to the ticket.",
    items: [
      {
        Icon: BagIcon,
        title: "Shopify sidebar",
        body:
          "Connect your Shopify store and the ticket sidebar shows the customer's profile, recent orders, fulfillment status and lifetime value.",
      },
      {
        Icon: GlobeIcon,
        title: "Zopping store",
        body:
          "For stores on Zopping: the customer's store profile, order count, lifetime spend and five most recent orders.",
      },
      {
        Icon: CodeIcon,
        title: "Customer data lookup",
        body:
          "Point ZopDesk at an HTTPS endpoint in your own system. It calls it with signed requests, and your fields and links show on every ticket.",
      },
      {
        Icon: PlugIcon,
        title: "Ticket actions",
        body:
          "Buttons on tickets, like Create lead, that send the ticket to your own system. The answer is recorded as an internal note, and the ticket can be solved automatically.",
      },
    ],
  },
  {
    id: "team",
    heading: "Team and access",
    Icon: UsersIcon,
    subheading: "Google sign-in and four clear roles.",
    items: [
      {
        Icon: UsersIcon,
        title: "Sign in with Google",
        body:
          "No passwords. Invite teammates by email from Settings.",
      },
      {
        Icon: LayersIcon,
        title: "Optional workspace auto-join",
        body:
          "Turn it on and colleagues on your Google Workspace domain join as viewers when they first sign in. It's off by default.",
      },
      {
        Icon: ShieldIcon,
        title: "Owner, admin, agent, viewer",
        body:
          "Owners and admins manage settings, agents work tickets, viewers can read. There's one owner, and ownership can be transferred.",
      },
    ],
  },
];

const roadmap: {
  label: string;
  Icon: IconType;
}[] = [
  { label: "Live chat widget", Icon: ChatIcon },
  { label: "Messenger and Instagram messages", Icon: ChatIcon },
  { label: "Customer portal", Icon: PortalIcon },
  { label: "Knowledge base", Icon: BookIcon },
  { label: "SLA breach alerts", Icon: StopwatchIcon },
  { label: "Importing tickets from another helpdesk", Icon: RefreshIcon },
];

const designPoints = [
  { Icon: LayersIcon, title: "8 services", body: "Each deploys on its own; no service reads another's database." },
  { Icon: ShieldIcon, title: "Scoped by org", body: "Every tenant row carries your org id, checked on every request." },
  { Icon: RefreshIcon, title: "Failures stay contained", body: "If an AI provider or Shopify is unreachable, tickets and replies keep working." },
  { Icon: GaugeIcon, title: "Hosted on Google Cloud", body: "In the US. There's no region choice yet." },
];

export default function Features() {
  usePageMeta("/features");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            Product
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            What ZopDesk does <br className="hidden sm:block" />
            today.
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            ZopDesk is in beta. This page lists what's built and in daily use:
            channels, ticketing, saved replies, rules, SLA targets, surveys and
            reports, AI assist with your own key, store context, and an API. What
            isn't built yet is listed at the end.
          </p>

          <div
            className="mt-10 flex animate-fade-up flex-wrap gap-2"
            style={{ animationDelay: "220ms" }}
          >
            {groups.map((g) => (
              <a
                key={g.id}
                href={`#${g.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-200 backdrop-blur transition hover:-translate-y-0.5 hover:border-brand-300/40 hover:bg-white/10 hover:text-white"
              >
                <g.Icon width={14} height={14} />
                {g.heading}
              </a>
            ))}
          </div>
        </div>
      </section>


      <section className="relative border-y border-ink-200 bg-gradient-to-br from-white via-brand-50/40 to-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Channels", value: "3", sub: "Email, WhatsApp and phone (Acefone)" },
              { label: "Roles", value: "4", sub: "Owner, admin, agent and viewer" },
              { label: "Attachment size", value: "10 MB", sub: "Per file, on replies and notes" },
              { label: "During the beta", value: "Free", sub: "Every feature on this page" },
            ].map((s, i) => (
              <div
                key={s.label}
                className="animate-fade-up border-l-2 border-brand-500 pl-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="bg-gradient-to-br from-brand-600 to-violet-600 bg-clip-text text-4xl font-semibold tracking-tight text-transparent">
                  {s.value}
                </p>
                <p className="mt-2 text-sm font-medium text-ink-900">{s.label}</p>
                <p className="mt-0.5 text-xs text-ink-500">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="relative">
        {groups.map(({ id, heading, Icon, subheading, items }, gIdx) => {
          const tinted = gIdx % 2 === 1;
          return (
            <section
              key={id}
              id={id}
              className={`scroll-mt-24 ${
                tinted
                  ? "border-y border-ink-100 bg-gradient-to-br from-brand-50/60 via-white to-violet-50/50"
                  : "bg-white"
              }`}
            >
              <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
                <div className="mb-10 max-w-2xl">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="relative inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-lg shadow-brand-500/20">
                      <Icon width={22} height={22} />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                      {String(gIdx + 1).padStart(2, "0")} · Module
                    </p>
                  </div>
                  <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
                    {heading}
                  </h2>
                  <p className="mt-3 text-lg text-ink-600">{subheading}</p>
                </div>
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <div
                      key={item.title}
                      className="group rounded-xl border border-ink-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
                    >
                      <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-brand-50 text-brand-700 ring-1 ring-brand-100 transition group-hover:bg-brand-100 group-hover:ring-brand-200">
                        <item.Icon width={18} height={18} />
                      </div>
                      <h3 className="mb-2 text-base font-semibold text-ink-900">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-ink-600">
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section className="border-y border-ink-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
                How it's built
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
                Eight services, one product.
              </h2>
              <p className="mt-4 text-ink-600">
                The backend is eight Go services, each deployed on its own. Six
                of them own a MySQL database that no other service reads; they
                talk to each other over HTTP. There is no event bus.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {designPoints.map(({ Icon, title, body }) => (
                  <div
                    key={title}
                    className="rounded-xl border border-ink-200 bg-ink-50 p-4"
                  >
                    <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white text-brand-700 ring-1 ring-ink-200">
                      <Icon width={16} height={16} />
                    </div>
                    <p className="text-sm font-semibold text-ink-900">{title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-ink-600">
                      {body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-ink-200 bg-ink-50 p-6 sm:p-8">
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  ["Gateway", "no database"],
                  ["Identity", "db: identity"],
                  ["Customers", "db: customers"],
                  ["Ticketing", "db: ticketing"],
                  ["Email gateway", "db: email_gateway"],
                  ["Integrations", "db: integrations"],
                  ["AI assist", "db: ai_assist"],
                  ["Internal", "no database"],
                ].map(([svc, db], i) => (
                  <div
                    key={svc}
                    className="rounded-lg border border-ink-200 bg-white p-3 text-center text-xs font-semibold text-ink-800 shadow-sm"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className="mx-auto mb-1.5 h-6 w-6 rounded-md bg-gradient-to-br from-brand-500/20 to-indigo-500/20" />
                    {svc}
                    <p className="mt-1 font-mono text-[10px] font-normal text-ink-500">
                      {db}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-dashed border-ink-300 bg-white p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Between services
                </p>
                <p className="font-mono text-[11px] text-ink-700">
                  HTTP + JSON, org id carried on every call
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              Developer friendly
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              An API for tickets and customers.
            </h2>
            <p className="mt-4 text-ink-600">
              Read and create tickets, customers and companies from your own
              tools with an API key, and get a signed webhook when tickets
              change. Settings, team and channels stay in the app.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ink-700">
              {[
                "API keys with scopes: tickets:read, tickets:write, tickets:reply",
                "Webhooks for ticket created, updated and solved, and new messages",
                "HMAC-SHA256 signatures; failed deliveries retried for about a day",
                "Customer data lookup and ticket actions for your own systems",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                    <CheckIcon width={12} height={12} strokeWidth={2.4} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-ink-900 text-ink-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-xs font-mono text-ink-300">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
              <span className="ml-3">POST /tickets/tkt_123/events</span>
            </div>
            <pre className="overflow-x-auto p-5 font-mono text-xs leading-relaxed">
{`curl https://api.zopdesk.com/tickets/tkt_123/events \\
  -H "Authorization: Bearer \${ZOPDESK_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"internal_note",
       "body":"Refund approved in the console."}'

# webhook, signed with HMAC-SHA256
POST https://you.example.com/hooks
X-ZopDesk-Event: ticket.updated
X-ZopDesk-Signature: sha256=9f2a...
{
  "type": "ticket.updated",
  "data": {
    "ticket": { "id": "tkt_123", "status": "pending" },
    "changes": { "status": { "old": "open", "new": "pending" } }
  }
}`}
            </pre>
          </div>
        </div>
        <Link
          to="/developers"
          className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-600"
        >
          Read the developer docs
          <ArrowRightIcon width={14} height={14} />
        </Link>
      </section>

      <section className="border-y border-ink-200 bg-gradient-to-br from-ink-50 via-white to-brand-50/60">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              On the roadmap
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Not available yet.
            </h2>
            <p className="mt-3 text-ink-600">
              These are planned, not built. If one of them decides whether
              ZopDesk works for you, tell us; it helps us decide what comes
              first.
            </p>
          </div>
          <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {roadmap.map(({ label, Icon }) => (
              <li
                key={label}
                className="group flex items-start gap-3 rounded-xl border border-ink-200 bg-white px-4 py-3.5 text-sm text-ink-700 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-md bg-gradient-to-br from-brand-50 to-violet-50 text-brand-700 ring-1 ring-brand-100 transition group-hover:from-brand-100 group-hover:to-violet-100">
                  <Icon width={16} height={16} />
                </span>
                <span className="self-center">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-ink-800 bg-ink-900 p-10 text-center shadow-xl sm:p-14">
          <div className="pointer-events-none absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 right-0 h-60 w-60 rounded-full bg-violet-500/15 blur-3xl" />
          <div className="relative">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-300">
              Try it
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              See it with your own inbox.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-300">
              Free during the beta. Sign in with Google, forward a test address
              or connect a mailbox, and send yourself a ticket.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.appUrl}
                className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-xl hover:shadow-brand-500/40"
              >
                Start free
                <ArrowRightIcon
                  width={16}
                  height={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
              <Link
                to="/contact"
                className="rounded-md border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-ink-100 backdrop-blur transition hover:border-white/25 hover:bg-white/10"
              >
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
